import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
	SmartField_Api_Contracts_CoordinateDto,
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState,
} from '../../api';
import { experimentalLocationGetTreatments, experimentalLocationUpdateTreatment } from '../../api';
import { toLocalStartIso, toLocalEndIso } from '../../utils/dateUtils';

type CreateData = {
	state: SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState;
	friendlyName?: string | null;
	plotId?: number | null;
	fieldId?: number | null;
	shapeCoordinates: SmartField_Api_Contracts_CoordinateDto[];
	validFrom?: string | null;
	validTo?: string | null;
};

type UpdateData = {
	friendlyName?: string | null;
	plotId?: number | null;
	fieldId?: number | null;
	shapeCoordinates?: SmartField_Api_Contracts_CoordinateDto[] | null;
	validFrom?: string | null;
	validTo?: string | null;
};

type Props = {
	siteId: number;
	el?: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto;
	state: SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState;
	drawnCoords?: SmartField_Api_Contracts_CoordinateDto[];
	onCreate: (data: CreateData) => void;
	onUpdate: (data: UpdateData) => void;
	onRedraw?: () => void;
	onCancel: () => void;
	isSaving: boolean;
};

function toDateInputValue(iso?: string | null): string {
	if (!iso) {
		return new Date().toISOString().slice(0, 10);
	}
	return iso.slice(0, 10);
}

export function ExperimentalLocationFormPanel({ siteId, el, state, drawnCoords, onCreate, onUpdate, onRedraw, onCancel, isSaving }: Props) {
	const isNew = !el;
	const isPlot = state === 'Plot';
	const queryClient = useQueryClient();

	const { data: treatments } = useQuery({
		queryKey: ['treatments', siteId, el?.id],
		queryFn: () => experimentalLocationGetTreatments({ siteId, id: el!.id! }),
		enabled: !isNew && isPlot && el?.id != null,
	});

	const [treatmentNames, setTreatmentNames] = useState<Record<number, string>>({});

	const updateTreatmentMutation = useMutation({
		mutationFn: ({ treatmentId, friendlyName }: { treatmentId: number; friendlyName: string }) =>
			experimentalLocationUpdateTreatment({ siteId, id: el!.id!, treatmentId, requestBody: { friendlyName: friendlyName || null } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['treatments', siteId, el?.id] });
			toast.success('Treatment name saved.');
		},
		onError: () => toast.error('Failed to save treatment name.'),
	});

	const [friendlyName, setFriendlyName] = useState(el?.friendlyName ?? '');
	const [number, setNumber] = useState<string>(
		isPlot ? (el?.plotId != null ? String(el.plotId) : '') : (el?.fieldId != null ? String(el.fieldId) : '')
	);
	const [validFrom, setValidFrom] = useState<string>(toDateInputValue(el?.validFrom));
	const [validTo, setValidTo] = useState<string>(el?.validTo ? el.validTo.slice(0, 10) : '');

	const label = isPlot ? 'Plot' : 'Field';
	const numLabel = isPlot ? 'Plot number' : 'Field ID';

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const validToValue = validTo ? toLocalEndIso(validTo) : null;
		const validFromValue = validFrom ? toLocalStartIso(validFrom) : null;

		if (validFromValue && validToValue && validToValue <= validFromValue) {
			toast.error('Valid to must be later than Valid from.');
			return;
		}

		if (isNew) {
			if (!drawnCoords || drawnCoords.length < 3) {
				return;
			}
			onCreate({
				state,
				friendlyName: friendlyName || null,
				plotId: isPlot && number ? Number(number) : null,
				fieldId: !isPlot && number ? Number(number) : null,
				shapeCoordinates: drawnCoords,
				validFrom: validFromValue,
				validTo: validToValue,
			});
		} else {
			onUpdate({
				friendlyName: friendlyName || null,
				plotId: isPlot && number ? Number(number) : null,
				fieldId: !isPlot && number ? Number(number) : null,
				shapeCoordinates: el.shapeCoordinates ?? null,
				validFrom: validFromValue,
				validTo: validToValue,
			});
		}
	};

	const title = isNew
		? `New ${label}`
		: `Edit ${label} — ${el.friendlyName ?? el.entityIdentifier ?? `${label} ${isPlot ? el.plotId : el.fieldId}`}`;

	return (
		<div className="p-5">
			<h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
			{isNew && (
				<p className="text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded px-3 py-2 mb-3">
					Draw the polygon on the map, then fill in the details and save.
				</p>
			)}
			{isNew && (!drawnCoords || drawnCoords.length < 3) && (
				<p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
					Draw polygon on map first.
				</p>
			)}
			<form onSubmit={handleSubmit} className="space-y-3">
				<div>
					<label htmlFor="el-friendly-name" className="block text-sm font-medium text-gray-700 mb-1">Friendly name</label>
					<input
						id="el-friendly-name"
						type="text"
						value={friendlyName}
						onChange={(e) => setFriendlyName(e.target.value)}
						placeholder={`e.g. ${isPlot ? 'P01 — Control' : 'Field A'}`}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
					/>
				</div>
				<div>
					<label htmlFor="el-number" className="block text-sm font-medium text-gray-700 mb-1">{numLabel}</label>
					<input
						id="el-number"
						type="number"
						value={number}
						onChange={(e) => setNumber(e.target.value)}
						min={1}
						placeholder="Auto-assigned if blank"
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
					/>
				</div>
				<div>
					<label htmlFor="el-valid-from" className="block text-sm font-medium text-gray-700 mb-1">Valid from</label>
					<input
						id="el-valid-from"
						type="date"
						value={validFrom}
						onChange={(e) => setValidFrom(e.target.value)}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
					/>
				</div>
				<div>
					<label htmlFor="el-valid-to" className="block text-sm font-medium text-gray-700 mb-1">Valid to (blank = indefinite)</label>
					<input
						id="el-valid-to"
						type="date"
						value={validTo}
						onChange={(e) => setValidTo(e.target.value)}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
					/>
				</div>
				{!isNew && isPlot && treatments && treatments.length > 0 && (
					<div>
						<p className="block text-sm font-medium text-gray-700 mb-1">Treatments</p>
						<div className="space-y-2">
							{treatments.map((t) => {
								const tid = t.id!;
								const localName = treatmentNames[tid] ?? t.friendlyName ?? '';
								return (
									<div key={tid} className="flex items-center gap-2">
										<span className="text-sm text-gray-500 w-6 shrink-0">{t.treatmentNumber}</span>
										<input
											type="text"
											value={localName}
											onChange={(e) => setTreatmentNames((prev) => ({ ...prev, [tid]: e.target.value }))}
											onBlur={(e) => {
												const newName = e.target.value;
												if (newName !== (t.friendlyName ?? '')) {
													updateTreatmentMutation.mutate({ treatmentId: tid, friendlyName: newName });
												}
											}}
											placeholder={`Treatment ${t.treatmentNumber}`}
											className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
										/>
									</div>
								);
							})}
						</div>
					</div>
				)}
				<div className="flex gap-2 pt-1">
					<button
						type="submit"
						disabled={isSaving || (isNew && (!drawnCoords || drawnCoords.length < 3))}
						className="flex-1 px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
					>
						{isSaving ? 'Saving…' : 'Save'}
					</button>
					{!isNew && onRedraw && (
						<button
							type="button"
							onClick={onRedraw}
							className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
						>
							Redraw on map
						</button>
					)}
					<button
						type="button"
						onClick={onCancel}
						className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	);
}
